import {
  ActionType,
  createReducer,
  createStandardAction as action,
  PayloadAction
} from "typesafe-actions";
import gql from "graphql-tag";
import { Operation, OperationResult } from "@urql/core";
import { DocumentNode, OperationDefinitionNode } from "graphql";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";

/**
 * Our graphql store.  We do not need to cache the graphql data, this is for dispatching events
 * that happen withing|to graphql.  Such as when an error occurs.
 */
export type StoredOperation = Pick<Operation, "key" | "kind" | "context">;
export type StoredOperationResult = {
  name: string;
  at: Date;
  operation: StoredOperation;
  anyErrors: boolean;
};
export const clear = action("CLEAR_GRAPH")<string>();
export const operationCompleted = action("OPERATION_COMPLETED")<
  Omit<OperationResult, "data">
>();
export type GraphqlActions = ActionType<typeof operationCompleted>;
export const useGraphqlActions = (): Dispatch<GraphqlActions> =>
  useDispatch<Dispatch<GraphqlActions>>();

export interface GraphQlState {
  readonly recentOperations: Array<StoredOperationResult>;
}

export const defaultGraphQlState: GraphQlState = {
  recentOperations: []
};

/**
 * Get the query name from the document if it is available.
 * @param d
 */
export const getDocName = (d: DocumentNode) => {
  const def = d.definitions.find(x => {
    return x.kind === "OperationDefinition";
  });
  if (def) {
    const name = (def as OperationDefinitionNode).name;
    if (name) {
      return name.value;
    }
    return "Anonymous";
  }
  return "unknownQuery";
};

export function getId(q: string | DocumentNode): string {
  let name = "Unknown";
  try {
    if (typeof q === "string") {
      name = getDocName(gql(q));
    } else {
      const d = q as DocumentNode;
      name = getDocName(d);
    }
  } catch (e) {}
  return name;
}

const storeLimit = 10;
export function rememberOperation(
  { recentOperations }: GraphQlState,
  {
    payload: {
      operation: { kind, query, key, context },
      error
    }
  }: PayloadAction<string, Omit<OperationResult, "data">>
): GraphQlState {
  if (kind === "subscription" || kind === "teardown") {
    return { recentOperations };
  }
  const name = getId(query);
  const keep = storeLimit - 1;
  const upToLastX =
    recentOperations.length > keep
      ? recentOperations.slice(1, storeLimit)
      : recentOperations;
  const operation: StoredOperation = {
    kind,
    key,
    context
  };
  return {
    recentOperations: [
      ...upToLastX,
      { name, operation, at: new Date(), anyErrors: !!error }
    ]
  };
}

export const GraphQlReducer = createReducer(defaultGraphQlState).handleAction(
  operationCompleted,
  rememberOperation
);
